import { api } from "../../lib";

export interface SurgeryCenterPhysicianLink {
  id: string;
  surgeryCenterLabId: string;
  physicianId: string;
  isPrimary: boolean;
  createdAt: string;
  physician: {
    id: string;
    firstName: string;
    lastName: string;
    npi: string | null;
    email: string | null;
  };
}

export interface SurgeryCenterLab {
  id: string;
  name: string;
  type: "surgery_center" | "lab";
  address: string | null;
  primaryPhone: string | null;
  secondaryPhone: string | null;
  fax: string | null;
  medicalDirector: string | null;
  footerHtml: string | null;
  externalCaseNumberPrefix: string | null;
  preferredPathologistId: string | null;
  preferredPathologist?: {
    id: string;
    fullName: string;
  } | null;
  logoS3Key?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SurgeryCenterLabReportDeliveryMethod {
  id: string;
  surgeryCenterLabId: string;
  reportDeliveryMethodId: string;
  phone: string | null;
  fax: string | null;
  address: string | null;
  email: string | null;
  isActive: boolean;
  // HL7 endpoint config (only for HL7 delivery methods)
  hl7EndpointHost: string | null;
  hl7EndpointPort: number | null;
  hl7FacilityId: string | null;
  hl7ApplicationId: string | null;
  remoteConnectionIdentifier?: string | null;
  createdAt: string;
  updatedAt: string;
  reportDeliveryMethod?: {
    id: string;
    name: string;
  };
}

export interface CreateSurgeryCenterLabReportDeliveryMethodInput {
  reportDeliveryMethodId: string;
  phone?: string | null;
  fax?: string | null;
  address?: string | null;
  email?: string | null;
  hl7EndpointHost?: string | null;
  hl7EndpointPort?: number | null;
  hl7FacilityId?: string | null;
  hl7ApplicationId?: string | null;
  remoteConnectionIdentifier?: string | null;
}

export interface UpdateSurgeryCenterLabReportDeliveryMethodInput
  extends Partial<CreateSurgeryCenterLabReportDeliveryMethodInput> {
  id: string;
}

export interface CreateSurgeryCenterLabInput {
  name: string;
  type: "surgery_center" | "lab";
  address?: string | null;
  primaryPhone?: string | null;
  secondaryPhone?: string | null;
  fax?: string | null;
  medicalDirector?: string | null;
  footerHtml?: string | null;
  externalCaseNumberPrefix?: string | null;
  preferredPathologistId?: string | null;
  logoS3Key?: string | null;
  isActive?: boolean;
}

export interface UpdateSurgeryCenterLabInput
  extends Partial<CreateSurgeryCenterLabInput> {
  id: string;
}

export const surgeryCentersLabsService = {
  getAll: async () => {
    return api.get<SurgeryCenterLab[]>("/admin/surgery-centers-labs");
  },

  getById: async (id: string) => {
    return api.get<SurgeryCenterLab>(`/admin/surgery-centers-labs/${id}`);
  },

  getSurgeryCenters: async () => {
    return api.get<SurgeryCenterLab[]>("/admin/surgery-centers");
  },

  getLabs: async () => {
    return api.get<SurgeryCenterLab[]>("/admin/labs");
  },

  create: async (input: CreateSurgeryCenterLabInput) => {
    return api.post<SurgeryCenterLab>("/admin/surgery-centers-labs", input);
  },

  update: async (input: UpdateSurgeryCenterLabInput) => {
    const { id, ...updates } = input;
    return api.put<SurgeryCenterLab>(
      `/admin/surgery-centers-labs/${id}`,
      updates
    );
  },

  delete: async (id: string) => {
    await api.delete<void>(`/admin/surgery-centers-labs/${id}`);
  },

  toggleActive: async (id: string, isActive: boolean) => {
    return api.put<SurgeryCenterLab>(`/admin/surgery-centers-labs/${id}`, {
      isActive,
    });
  },

  // ── Physician links ──────────────────────────────────────────────────────

  getPhysicians: async (surgeryCenterLabId: string) => {
    return api.get<SurgeryCenterPhysicianLink[]>(
      `/admin/surgery-centers-labs/${surgeryCenterLabId}/physicians`
    );
  },

  addPhysician: async (
    surgeryCenterLabId: string,
    input: { physicianId: string; isPrimary?: boolean }
  ) => {
    return api.post<SurgeryCenterPhysicianLink>(
      `/admin/surgery-centers-labs/${surgeryCenterLabId}/physicians`,
      input
    );
  },

  setPrimaryPhysician: async (
    surgeryCenterLabId: string,
    physicianId: string
  ) => {
    return api.patch<SurgeryCenterPhysicianLink>(
      `/admin/surgery-centers-labs/${surgeryCenterLabId}/physicians/${physicianId}/set-primary`,
      {}
    );
  },

  removePhysician: async (surgeryCenterLabId: string, physicianId: string) => {
    await api.delete<void>(
      `/admin/surgery-centers-labs/${surgeryCenterLabId}/physicians/${physicianId}`
    );
  },

  // ── Report delivery methods ────────────────────────────────────────────────

  getReportDeliveryMethods: async (surgeryCenterLabId: string) => {
    return api.get<SurgeryCenterLabReportDeliveryMethod[]>(
      `/admin/surgery-centers-labs/${surgeryCenterLabId}/report-delivery-methods`
    );
  },

  addReportDeliveryMethod: async (
    surgeryCenterLabId: string,
    input: CreateSurgeryCenterLabReportDeliveryMethodInput
  ) => {
    return api.post<SurgeryCenterLabReportDeliveryMethod>(
      `/admin/surgery-centers-labs/${surgeryCenterLabId}/report-delivery-methods`,
      input
    );
  },

  updateReportDeliveryMethod: async (
    surgeryCenterLabId: string,
    input: UpdateSurgeryCenterLabReportDeliveryMethodInput
  ) => {
    const { id, ...updates } = input;
    return api.put<SurgeryCenterLabReportDeliveryMethod>(
      `/admin/surgery-centers-labs/${surgeryCenterLabId}/report-delivery-methods/${id}`,
      updates
    );
  },

  removeReportDeliveryMethod: async (
    surgeryCenterLabId: string,
    id: string
  ) => {
    await api.delete<void>(
      `/admin/surgery-centers-labs/${surgeryCenterLabId}/report-delivery-methods/${id}`
    );
  },

  toggleReportDeliveryMethodActive: async (
    surgeryCenterLabId: string,
    id: string
  ) => {
    return api.patch<SurgeryCenterLabReportDeliveryMethod>(
      `/admin/surgery-centers-labs/${surgeryCenterLabId}/report-delivery-methods/${id}/toggle-active`
    );
  },

  // ── Logo upload ────────────────────────────────────────────────────────────

  getLogoPresignedUrl: async (
    fileName: string,
    contentType: string
  ): Promise<{ url: string; key: string }> => {
    return api.post<{ url: string; key: string }>(
      "/documents/presigned-url/surgery-center-logos",
      { fileName, contentType }
    );
  },

  uploadLogoToS3: async (url: string, file: Blob): Promise<void> => {
    await fetch(url, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type || "image/png" },
    });
  },

  getLogoDownloadUrl: async (key: string): Promise<string> => {
    const response = await api.post<{ url: string }>(
      "/documents/download-url",
      {
        key,
      }
    );
    return response.url;
  },
};
